import Service from '@ember/service';

/**
 * Service for managing tour completion state.
 * Currently uses localStorage, but designed to be easily swapped for API-based storage.
 *
 * @memberof services
 */
export default class TourStorageService extends Service {
  private readonly STORAGE_KEY = 'tio-completed-tours';

  /** Reads the locally persisted set of completed tour IDs. */
  getCompletedTours(): Set<string> {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch (error) {
      console.error('Error reading completed tours from storage:', error);
      return new Set();
    }
  }

  isTourCompleted(tourId: string): boolean {
    return this.getCompletedTours().has(tourId);
  }

  /**
   * Mark a tour as completed.
   * In the future, this could POST to an API endpoint.
   */
  markTourCompleted(tourId: string) {
    try {
      const completed = this.getCompletedTours();
      completed.add(tourId);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify([...completed]));
    } catch (error) {
      console.error('Error saving completed tour to storage:', error);
    }
  }

  /**
   * Reset completion status for a tour (useful for testing).
   */
  resetTour(tourId: string) {
    try {
      const completed = this.getCompletedTours();
      completed.delete(tourId);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify([...completed]));
    } catch (error) {
      console.error('Error resetting tour in storage:', error);
    }
  }

  /**
   * Clear all completed tours.
   */
  clearAllTours() {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing tours from storage:', error);
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'tour-storage': TourStorageService;
  }
}
